import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { DocumentService } from "@/services/api/DocumentService";
import { PDFService } from "@/services/knowledgebase/PDFService";
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

export const useDeleteDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const success = await DocumentService.deleteKnowledgeBaseDocument(id);
      if (!success) throw new Error("Failed to delete document");
      return id;
    },
    onSuccess: () => {
      toast.success("Document deleted successfully.");
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["user-agents"] });
      queryClient.invalidateQueries({ queryKey: ["public-help-hub"] });
      queryClient.invalidateQueries({ queryKey: ["public-help-guide"] });
    },
    onError: (error) => {
      console.error("Failed to delete document:", error);
      toast.error("Failed to delete document.");
    },
  });
};

export const useUpdateDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      document,
      updates,
    }: {
      document: DocumentWithMetadata;
      updates: Partial<DocumentWithMetadata>;
    }) => {
      const serviceUpdates = {
        name: updates.name,
        ...(updates.is_active !== undefined && {
          status: updates.is_active ? "active" : "inactive",
        }),
        isPublic: updates.is_public,
        metadata: updates.metadata ? { ...document.metadata, ...updates.metadata } : document.metadata,
        ...(updates.folder_id !== undefined && { folderId: updates.folder_id }),
        ...(updates.user_id !== undefined && { userId: updates.user_id }),
      };

      const updatedDoc = await DocumentService.updateKnowledgeBaseDocument(document.id, serviceUpdates);
      if (!updatedDoc) throw new Error("Failed to update document");
      return updatedDoc;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });
      queryClient.invalidateQueries({ queryKey: ["document-folders"] });
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["agent"] });
      queryClient.invalidateQueries({ queryKey: ["user-agents"] });
      queryClient.invalidateQueries({ queryKey: ["all-agents"] });
      // Publish/unpublish, renames and folder moves all change the public page.
      queryClient.invalidateQueries({ queryKey: ["public-help-hub"] });
      queryClient.invalidateQueries({ queryKey: ["public-help-guide"] });
    },
    onError: (error) => {
      console.error("Failed to update document:", error);
      toast.error("Failed to update document.");
    },
  });
};

// TODO maybe we can delete this as the process is autometed. Or refactor to use API
export const useSendDocumentToElevenLabs = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (doc: DocumentWithMetadata) => {
      const sendingToast = toast.loading(`Sending "${doc.name}" to knowledge base...`);

      try {
        if (doc.el_doc_id) {
          await supabase.functions.invoke("elevenlabs", {
            body: { resource: "knowledge-base", action: "delete", params: { entityId: doc.el_doc_id } },
          });
        }

        let content = "";

        if (PDFService.isPdf(doc)) {
          const metadataResponse = await fetch(`${doc.source_path}/metadata.json`);

          if (metadataResponse.ok) {
            const metadata = await metadataResponse.json();
            const totalParts = metadata.parts || 1;
            const contentParts = [];

            let useOptimized = true;
            for (let i = 1; i <= totalParts; i++) {
              const optimizedUrl = `${doc.source_path}/optimized_${i}.txt`;
              const response = await fetch(optimizedUrl);

              if (!response.ok) {
                useOptimized = false;
                break;
              }

              contentParts.push(await response.text());
            }

            if (!useOptimized) {
              contentParts.length = 0;
              for (let i = 1; i <= totalParts; i++) {
                const parsedUrl = `${doc.source_path}/parsed_${i}.txt`;
                const response = await fetch(parsedUrl);

                if (response.ok) {
                  contentParts.push(await response.text());
                }
              }
            }

            if (contentParts.length > 0) {
              content = contentParts.join("\n\n");
            }
          } else {
            const response = await fetch(doc.parsed_path);
            if (response.ok) {
              content = await response.text();
            }
          }
        } else if (doc.parsed_path) {
          const signedUrl = await DocumentService.getSignedUrl(doc.parsed_path);
          if (signedUrl) {
            const response = await fetch(signedUrl);
            if (response.ok) {
              content = await response.text();
            }
          }
        }

        if (!content) {
          toast.error("No content found for this document", { id: sendingToast });
          return;
        }

        const { data: kbDoc, error: kbError } = await supabase.functions.invoke("elevenlabs", {
          body: { resource: "knowledge-base", action: "create-text", params: { text: content, name: doc.name } },
        });
        if (kbError) throw kbError;

        await DocumentService.updateKnowledgeBaseDocument(doc.id, {
          elDocId: kbDoc.id,
        });

        toast.success("Successfully sent to EL Knowledge Base", { id: sendingToast });

        queryClient.invalidateQueries({ queryKey: ["documents"] });
        queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
        queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });
        queryClient.invalidateQueries({ queryKey: ["user-agents"] });
      } catch (error) {
        console.error("Failed to send to EL knowledge base:", error);
        toast.error("Failed to send to knowledge base", { id: sendingToast });
        throw error;
      }
    },
    [queryClient],
  );
};

export const useRemoveDocumentFromKB = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (doc: DocumentWithMetadata) => {
      if (!doc.el_doc_id) return;

      const removeToast = toast.loading(`Removing "${doc.name}" from knowledge base...`);

      try {
        await supabase.functions.invoke("elevenlabs", {
          body: { resource: "knowledge-base", action: "delete", params: { entityId: doc.el_doc_id } },
        });

        await DocumentService.updateKnowledgeBaseDocument(doc.id, {
          elDocId: null,
        });

        toast.success("Successfully removed from knowledge base", { id: removeToast });

        queryClient.invalidateQueries({ queryKey: ["documents"] });
        queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
        queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });
        queryClient.invalidateQueries({ queryKey: ["user-agents"] });
      } catch (error) {
        console.error("Failed to remove from EL knowledge base:", error);
        toast.error("Failed to remove from knowledge base", { id: removeToast });
        throw error;
      }
    },
    [queryClient],
  );
};

export const useUpdateDocumentContent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentId,
      content,
      isOptimized = false,
    }: {
      documentId: string;
      content: string;
      isOptimized?: boolean;
    }) => {
      const result = await DocumentService.updateDocumentContent(documentId, content, isOptimized);
      if (!result.success) throw new Error("Failed to update document content");
      return result;
    },
    onSuccess: () => {
      toast.success("Document content updated successfully");
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["user-agents"] });
      queryClient.invalidateQueries({ queryKey: ["public-help-hub"] });
      queryClient.invalidateQueries({ queryKey: ["public-help-guide"] });
    },
    onError: (error) => {
      console.error("Failed to update document content:", error);
      toast.error("Failed to update document content");
    },
  });
};

export const useBulkUpdateDocuments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentIds,
      updates,
    }: {
      documentIds: string[];
      updates: {
        is_active?: boolean;
        is_public?: boolean;
        is_private?: boolean;
        folder_id?: string | null;
        consumer_genie_id?: string | null;
        status?: string;
        source_type?: string;
        user_id?: string;
      };
    }) => {
      const result = await DocumentService.bulkUpdateDocuments(documentIds, updates);
      if (!result.success) throw new Error("Failed to bulk update documents");
      return result;
    },
    onSuccess: (data) => {
      toast.success(`Successfully updated ${data.updatedCount} document(s)`);
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });
      queryClient.invalidateQueries({ queryKey: ["document-folders"] });
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["user-agents"] });
      queryClient.invalidateQueries({ queryKey: ["public-help-hub"] });
      queryClient.invalidateQueries({ queryKey: ["public-help-guide"] });
    },
    onError: (error) => {
      console.error("Failed to bulk update documents:", error);
      toast.error("Failed to bulk update documents");
    },
  });
};

export const useBulkDeleteDocuments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentIds: string[]) => {
      const results = await Promise.allSettled(
        documentIds.map((id) => DocumentService.deleteKnowledgeBaseDocument(id)),
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed === documentIds.length) throw new Error("Failed to delete all documents");
      return { deleted: documentIds.length - failed, failed };
    },
    onSuccess: ({ deleted, failed }) => {
      if (failed > 0) {
        toast.warning(`Deleted ${deleted} document(s), ${failed} failed`);
      } else {
        toast.success(`Successfully deleted ${deleted} document(s)`);
      }
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });
      queryClient.invalidateQueries({ queryKey: ["document-folders"] });
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["user-agents"] });
      queryClient.invalidateQueries({ queryKey: ["public-help-hub"] });
      queryClient.invalidateQueries({ queryKey: ["public-help-guide"] });
    },
    onError: (error) => {
      console.error("Failed to bulk delete documents:", error);
      toast.error("Failed to delete documents");
    },
  });
};

export const useCloneDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ documentId, name, userId }: { documentId: string; name?: string; userId?: string }) => {
      const clonedDoc = await DocumentService.cloneDocument(documentId, name, userId);
      if (!clonedDoc) throw new Error("Failed to clone document");
      return clonedDoc;
    },
    onSuccess: (clonedDoc) => {
      toast.success(`Document "${clonedDoc.name}" cloned successfully`);
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });
    },
    onError: (error) => {
      console.error("Failed to clone document:", error);
      toast.error("Failed to clone document");
    },
  });
};

export const useDetachDocumentFromAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      elDocIds,
      documentIds,
    }: {
      agentId: string;
      elDocIds: string[]; // ElevenLabs document IDs (el_doc_id) for KB filtering
      documentIds?: string[]; // Database document IDs for agent_document cleanup
    }) => {
      const result = await DocumentService.detachDocumentsFromAgent(agentId, { elDocIds, documentIds });
      if (!result.success) throw new Error("Failed to detach documents from agent");
      return result;
    },
    onSuccess: (data) => {
      toast.success(`Unlinked ${data.detachedCount} document(s) from genie`);
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["user-agents"] });
    },
    onError: (error) => {
      console.error("Failed to detach documents from agent:", error);
      toast.error("Failed to unlink documents from genie");
    },
  });
};

export const useConsolidateDocuments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentIds,
      prune = false,
      docName,
    }: {
      documentIds: string[];
      prune?: boolean;
      docName?: string;
    }) => {
      const result = await DocumentService.consolidateDocuments({
        documentIds,
        prune,
        docName,
      });
      if (!result.success) throw new Error(result.error || "Failed to consolidate documents");
      return result;
    },
    onSuccess: (data) => {
      toast.success(data.message || `Successfully consolidated documents into ${data.parts} file(s)`);
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["user-agents"] });
    },
    onError: (error) => {
      console.error("Failed to consolidate documents:", error);
      toast.error("Failed to consolidate documents");
    },
  });
};
